import ContextMenu from "dashboard/_shared/components/ContextMenu/ContextMenu";
import {
  createContext,
  HTMLAttributes,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from "react-beautiful-dnd";
import { Link } from "react-router-dom";
import RenameTab from "./RenameTab";
import styles from "./NavigationTabs.module.scss";
import { API } from "aws-amplify";

const TabsContext = createContext({
  renamingId: null as string | null,
  cancelRename: () => { },
  selectedContextId: null as string | null,
  setSelectedContextId: (_id: string) => { },
  setCustomContextItems: (_items: ContextItem[]) => { },
  isDraggable: false,
});

type ContextItem = {
  label: string;
  action: (id: string, payload?: any) => void;
  type?: "rename" | "custom";
};

interface Props {
  children: ReactElement<TabProps> | ReactElement<TabProps>[];
  onDragEnd?: OnDragEndResponder;
  contextItems?: ContextItem[];
}

function NavigationTabs({
  children,
  contextItems = [],
  onDragEnd,
  ...rest
}: Props & Omit<HTMLAttributes<HTMLDivElement>, "onDragEnd">) {
  const isDraggable = !!onDragEnd;
  const hoverRef = useRef<NodeJS.Timeout>();
  const [isHovered, setIsHovered] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [selectedContextId, setSelectedContextId] = useState<string | null>(
    null,
  );
  const [customContextItems, setCustomContextItems] = useState<ContextItem[]>(
    [],
  );

  const onMouseEnter = () => {
    setIsHovered(true);
    if (hoverRef.current) {
      clearTimeout(hoverRef.current);
    }
  };
  const onMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsHovered(false);
    }, 2000);
    hoverRef.current = timeout;
  };

  return (
    <ContextMenu>
      <nav {...rest}>
        <ContextMenu.Menu>
          {
            [...customContextItems, ...contextItems]?.map((item) => (
              <ContextMenu.Item
                key={item.label}
                onClick={() => {
                  if (item.type === "rename") setRenamingId(selectedContextId);
                  item.action(selectedContextId ?? "");
                }}
              >
                {item.label}
              </ContextMenu.Item>
            )) as ReactElement<typeof ContextMenu.Item>[]
          }
        </ContextMenu.Menu>
        <TabsContext.Provider
          value={{
            renamingId,
            cancelRename: () => setRenamingId(null),
            selectedContextId,
            setSelectedContextId,
            setCustomContextItems,
            isDraggable,
          }}
        >
          {onDragEnd ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable" direction="horizontal">
                {(provided) => (
                  <div
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className={`${styles.wrapper} ${isHovered ? styles.focused : styles.unfocused
                      }`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {children}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={`${styles.wrapper} ${isHovered ? styles.focused : styles.unfocused
                }`}
            >
              {children}
            </div>
          )}
        </TabsContext.Provider>
      </nav>
    </ContextMenu>
  );
}

interface TabProps {
  id: string;
  label: string;
  index: number;
  selected: boolean;
  path: string;
  iconSrc?: string;
  tooltip?: string;
  contextItems?: ContextItem[];
  onRename?: (id: string, newName: string) => void;
}
const regimePanels = ["Macro Regime Reflation", "Macro Regime Deflation", "Macro Regime Growth", "Macro Regime Stagflation"];
function Tab({
  id,
  label,
  index,
  selected,
  path,
  iconSrc,
  tooltip,
  contextItems,
  onRename,
}: TabProps) {
  const {
    renamingId,
    cancelRename,
    setSelectedContextId,
    setCustomContextItems,
    isDraggable,
  } = useContext(TabsContext);
  const isRenaming = renamingId === id;

  const content = regimePanels.includes(label) ? <RegimePanelTab panel={label} /> : ['42 Macro', 'Options Insight', 'Herb Greenberg'].includes(label) ? <MacroPanelTab panel={label} /> : ['VIP View', 'Bullseye AI Trade Ideas'].includes(label) ? <VIPViewTab panel={label} /> : ['Chat'].includes(label) ? <ChatTab /> : (
    <>
      {iconSrc && !regimePanels.includes(label) && (
        <i className="fas">
          <img
            className="logo"
            src={iconSrc}
            style={{ height: 28, marginRight: 5 }}
            alt="tab logo"
          />
        </i>
      )}
      {isRenaming ? (
        <RenameTab
          originalName={label}
          onRename={(newName) => {
            onRename?.(id, newName);
            cancelRename();
          }}
          onCancel={cancelRename}
        />
      ) : (
        <div
          className="tab-text"
          style={{
            // TODO: clean this up
            // wordBreak: "break-word",
            width: [
              "Macro ETFs",
              "Top 25",
              "Index Futures",
              "Market Insights",
              "Crypto",
            ].includes(label)
              ? 60
              : [
                "ETF Heat Map & Fund Flows",
                "Global Commodity ETFs",
                "Global Currency ETFs",
                "Cryptocurrencies",
                "COMMODITIES",
                "FIXED INCOME",
                "Futures Contract Positioning",
              ].includes(label)
                ? 130
                : label.length < 12
                  ? 90
                  : label.length > 18
                    ? 120
                    : 90,
            lineHeight: 1.1,
          }}
        >
          {label.length < 29 ? label : label.substring(0, 24) + "..."}
        </div>
      )}
      {tooltip ? (
        <div
          style={{ color: "white" }}
          className="info-icon"
          data-tip={tooltip}
        >
          i
        </div>
      ) : (
        ""
      )}
    </>
  );
  if (!isDraggable)
    return (
      <Link
        data-testid="dashboard-tab"
        data-selected={selected || undefined}
        className={`${styles.tab}`}
        to={path}
        onContextMenu={() => {
          setSelectedContextId(id);
          const customContextItems = contextItems ? [...contextItems] : [];
          if (onRename)
            customContextItems.push({
              label: "Rename",
              type: "rename",
              action: () => console.log("rename"),
            });
          if (contextItems) setCustomContextItems(customContextItems);
        }}
      >
        {content}
      </Link>
    );
  return (
    <Draggable key={index} draggableId={`${index}-${path}`} index={index}>
      {(provided) => (
        <Link
          {...provided.dragHandleProps}
          data-testid="dashboard-tab"
          data-selected={selected || undefined}
          className={`${styles.tab} ${selected ? styles.selected : ""} ${selected ? 'selectedTab' : ''}`}
          to={path}
          ref={provided.innerRef}
          onContextMenu={() => {
            setSelectedContextId(id);
            const customContextItems = contextItems ? [...contextItems] : [];
            if (onRename)
              customContextItems.push({
                label: "Rename",
                type: "rename",
                action: () => console.log("rename"),
              });
            if (contextItems) setCustomContextItems(customContextItems);
          }}
          {...provided.draggableProps}
        >
          {content}
        </Link>
      )}
    </Draggable>
  );
}

NavigationTabs.Tab = Tab;

export default NavigationTabs;

const RegimePanelTab = ({ panel }) => {
  const [type, setType] = useState(null);
  const [strength, setStrength] = useState(0);
  const [alert, setAlert] = useState(false);

  useEffect(() => {
    const async = async () => {
      const response = await API.graphql({
        query: `{
        panel(where: {name: {_eq: "${panel}"}}) {
          tickers {
            data {
              algo_30D_ST_MOMENTUM
              algo_90D_MT_MOMENTUM
              algo_daily_CALC_TRADING_RANGE_SIGNAL
            }
          }
        }
      }`});
      let shortTermSum = 0;
      let mediumTermSum = 0;
      for (const { shortTerm, mediumTerm, rangeSignal } of response.data.panel[0].tickers.map(obj => ({ shortTerm: obj.data.algo_30D_ST_MOMENTUM, mediumTerm: obj.data.algo_90D_MT_MOMENTUM, rangeSignal: obj.data.algo_daily_CALC_TRADING_RANGE_SIGNAL }))) {
        if (shortTerm === 'BULLISH') { shortTermSum += 1; }
        if (shortTerm === 'NEUTRAL') { shortTermSum += 0; }
        if (shortTerm === 'BEARISH') { shortTermSum -= 1; }
        if (mediumTerm === 'BULLISH') { mediumTermSum += 1; }
        if (mediumTerm === 'NEUTRAL') { mediumTermSum += 0; }
        if (mediumTerm === 'BEARISH') { mediumTermSum -= 1; }
        if (["Sell", "Buy"].includes(rangeSignal)) {
          setAlert(true);
        }
      }
      const result = Math.round(((shortTermSum + mediumTermSum) / (response.data.panel[0].tickers.length * 2)) * 10);
      if (result > 0) {
        setType('BULLISH');
      }
      else if (result < 0) {
        setType('BEARISH');
      }
      setStrength(Math.abs(result));
    }
    async();
  }, []);

  const signalColor = (index) => {
    let primaryColor = "#ffffff";
    let halfColor = "linear-gradient(#3E3E3E 50%, #ffffff 50%)";
    let neutralColor = "#3E3E3E";
    if (type === "BULLISH") {
      primaryColor = "#75CE41";
      halfColor = "linear-gradient(#3E3E3E 50%, #75CE41 50%)";
      neutralColor = "#3E3E3E";
    }
    if (type === "BEARISH") {
      primaryColor = "#D33541";
      halfColor = "linear-gradient(#57262B 50%, #D33541 50%)";
      neutralColor = "#57262B";
    }
    const style = [
      [neutralColor, neutralColor, neutralColor, neutralColor, neutralColor],
      [halfColor, neutralColor, neutralColor, neutralColor, neutralColor],
      [primaryColor, neutralColor, neutralColor, neutralColor, neutralColor],
      [primaryColor, halfColor, neutralColor, neutralColor, neutralColor],
      [primaryColor, primaryColor, neutralColor, neutralColor, neutralColor],
      [primaryColor, primaryColor, halfColor, neutralColor, neutralColor],
      [primaryColor, primaryColor, primaryColor, neutralColor, neutralColor],
      [primaryColor, primaryColor, primaryColor, halfColor, neutralColor],
      [primaryColor, primaryColor, primaryColor, primaryColor, neutralColor],
      [primaryColor, primaryColor, primaryColor, primaryColor, halfColor],
      [primaryColor, primaryColor, primaryColor, primaryColor, primaryColor],
    ][strength || 0];
    return style[index];
  };
  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-around",
          width: 40,
          marginRight: 10,
        }}
      >
        <div
          style={{
            height: 10,
            width: 5,
            backgroundImage: signalColor(0),
            backgroundColor: signalColor(0),
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: 15,
            width: 5,
            backgroundImage: signalColor(1),
            backgroundColor: signalColor(1),
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: 20,
            width: 5,
            backgroundImage: signalColor(2).startsWith("l")
              ? signalColor(2)
              : "unset",
            backgroundColor: signalColor(2),
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: 25,
            width: 5,
            backgroundImage: signalColor(3),
            backgroundColor: signalColor(3),
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: 30,
            width: 5,
            backgroundImage: signalColor(4),
            backgroundColor: signalColor(4),
            borderRadius: 20,
          }}
        ></div>
      </div>
      <div
        className="tab-text"
        style={{ width: 120, lineHeight: 1.1, }}
      >
        {panel}
      </div>
      <div style={{ marginBottom: -20, marginRight: -20, marginLeft: -20, width: 32, filter: alert ? '' : 'grayscale(1)' }}>🚨</div>
    </>)
}

const MacroPanelTab = ({ panel }) => {
  const [alert, setAlert] = useState(false);

  useEffect(() => {
    const async = async () => {
      const response = await API.graphql({
        query: `{
        panel(where: {name: {_eq: "${panel}"}}) {
          tickers {
            data {
              algo_daily_CALC_TRADING_RANGE_SIGNAL
            }
          }
        }
      }`});
      for (const { rangeSignal } of response.data.panel[0].tickers.map(obj => ({ rangeSignal: obj.data.algo_daily_CALC_TRADING_RANGE_SIGNAL }))) {
        if (["Sell", "Buy"].includes(rangeSignal)) {
          setAlert(true);
        }
      }
    }
    async();
  }, []);

  return (
    <>
      <i className="fas">
        <img
          className="logo"
          src={panel === '42 Macro' ? '/42macro_tabs.svg' : panel === 'Options Insight' ? '/optionsinsight_tabs.png' : panel === 'Herb Greenberg' ? '/herb_tabs.png' : ''}
          style={{ height: 28, marginRight: 5, marginBottom: -5 }}
          alt="tab logo"
        />
      </i>
      <div
        className="tab-text"
        style={{ width: panel === 'Herb Greenberg' ? 130 : 120, lineHeight: 1.1, marginRight: panel === '42 Macro' ? -30 : -30 }}
      >
        {panel === '42 Macro' ? '42 Macro' : panel === 'Options Insight' ? <>Options<br />Insight</> : panel === 'Herb Greenberg' ? <>Herb<br />Greenberg</> : ''}
      </div>
      <div style={{ marginBottom: -20, marginRight: -20, marginLeft: -20, width: 32, filter: alert ? '' : 'grayscale(1)' }}>🚨</div>
    </>)
}


const VIPViewTab = ({ panel }) => {
  const [alert, setAlert] = useState(false);

  useEffect(() => {
    const async = async () => {
      let pxCloseDates = [];
      const date = new Date();
      for (let i = 0; i < 5; i++) {
        pxCloseDates.push(date.toISOString().split("T")[0]);
        date.setDate(date.getDate() - 1);
      }
      pxCloseDates = `[${pxCloseDates.map((obj) => `"${obj}"`).join(",")}]`;
      const response = await API.graphql({
        query: `{
          oversold: data3(where: {ticker: {_nilike: "%curncy"}, algo_daily_30D_ST_MOMENTUM: {_neq: "BEARISH"}, daily: {_eq: false}, px_close_dt: {_in: ${pxCloseDates}}, algo_${panel === "VIP View" ? "daily_" : ""}CALC_TRADING_RANGE_SIGNAL: {_eq: "Buy"}, algo_daily_CALC_TRADE_RANGE_ODDS: {_is_null: false}}) {                  
            algo_daily_CALC_TRADING_RANGE_SIGNAL
          }
          overbought: data3(where: {ticker: {_nilike: "%curncy"}, algo_daily_30D_ST_MOMENTUM: {_neq: "BULLISH"}, daily: {_eq: false}, px_close_dt: {_in: ${pxCloseDates}}, algo_${panel === "VIP View" ? "daily_" : ""}CALC_TRADING_RANGE_SIGNAL: {_eq: "Sell"}, algo_daily_CALC_TRADE_RANGE_ODDS: {_is_null: false}}) {                  
            algo_daily_CALC_TRADING_RANGE_SIGNAL
          }
        }`});
      for (const { rangeSignal } of [...response.data.oversold, response.data.overbought].map(obj => ({ rangeSignal: obj.algo_daily_CALC_TRADING_RANGE_SIGNAL }))) {
        if (["Sell", "Buy"].includes(rangeSignal)) {
          setAlert(true);
        }
      }
    }
    async();
  }, []);

  return (<>
    {panel === 'VIP View' ?
      <i className="fas" style={{ fontSize: 28, marginRight: 5 }}>{`✦`}</i>
      : <i className="fas">
        <img
          className="logo"
          src={'/bullseye.png'}
          style={{ height: 28, marginRight: 5, marginBottom: -5 }}
          alt="tab logo"
        />
      </i>
    }
    <div className="tab-text" style={{ lineHeight: 1.1, width: panel === 'VIP View' ? 70 : 90 }}>
      {panel}
    </div>
    <div
      style={{ color: "white", marginTop: -20, marginRight: 2 }}
      className="info-icon"
      data-tip={`The Bullseye Trade Ideas are generated from Longbow’s proprietary A.I. algorithms running on 2,000+ stock tickers throughout the trading day in order to identify highly probable, tactical Buy / Sell opportunities that can be monetized within 1-5 trading days.`}
    >
      i
    </div>
    <div style={{ marginBottom: -20, marginRight: -20, marginLeft: -20, width: 32, filter: alert ? '' : 'grayscale(1)' }}>🚨</div>
  </>)
}

const ChatTab = () => {
  return (
    <>
      <i className="fas">
        <img
          className="logo"
          src={'/chat.png'}
          style={{ height: 28, marginRight: 5, marginBottom: -5 }}
          alt="tab logo"
        />
      </i>
      <div
        className="tab-text"
        style={{ width: 80, lineHeight: 1.1 }}
      >
        {`Chat`}
      </div>
    </>)
}